import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ButtonProps,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { useTasksControllerGetLastModifiedTasksQuery } from 'src/app/services/api.generated';
import ResumeImageSvg from 'src/components/atoms/svg/ResumeImageSvg';
import UploadNewFileSvg from 'src/components/atoms/svg/UploadNewFileSvg';
import { useIsDemoUser } from 'src/components/hooks/useIsDemoUser';

type taskBoxType = {
  title: string;
  txt: string;
  icon: React.ReactNode;
  href: string;
  isDisable?: boolean;
  onClick?: () => unknown;
  btnVariant?: ButtonProps['variant'];
};

const ActionButton: FC<taskBoxType> = (props) => {
  const { title, txt, icon, href, isDisable, onClick, btnVariant } = props;
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      alignItems={{ xs: 'stretch', sm: 'center' }}
      justifyContent="space-between"
      gap={2}
    >
      <Stack direction="row" alignItems="center" gap={2} minWidth={0}>
        {icon}
        <Stack gap={0.5} minWidth={0}>
          <Typography fontSize={18} fontWeight={700} color="text.primary">
            {title}
          </Typography>
          <Typography fontSize={14} color="text.secondary">
            {txt}
          </Typography>
        </Stack>
      </Stack>
      <Link
        to={isDisable ? '' : href}
        style={{
          cursor: isDisable ? 'default' : 'pointer',
          textDecoration: 'none',
          flexShrink: 0,
        }}
        onClick={onClick}
      >
        <Button
          disabled={isDisable}
          variant={btnVariant || 'contained'}
          fullWidth
          sx={{ width: { xs: '100%', sm: 140 } }}
        >
          {title}
        </Button>
      </Link>
    </Stack>
  );
};

export const DashboardActions: FC = () => {
  const { t } = useTranslation();
  const isDemoUser = useIsDemoUser();

  const { data: tasks = [] } = useTasksControllerGetLastModifiedTasksQuery(
    {
      numberOfTasks: 1,
    },
    {
      skip: isDemoUser,
    }
  );

  const lastTask = useMemo(() => (tasks.length > 0 ? tasks[0] : null), [tasks]);

  const taskArray: taskBoxType[] = [
    {
      title: t('resumeTask'),
      txt: t('continueYourLatestTask'),
      icon: <ResumeImageSvg isDisable={!lastTask?.id} sx={{ fontSize: 44 }} />,
      href: '/edit/' + lastTask?.id,
      isDisable: !lastTask?.id,
    },
    {
      title: t('createTask'),
      txt: t('quicklyUploadAFileAndCreateANewTask'),
      icon: <UploadNewFileSvg sx={{ fontSize: 44 }} />,
      href: '/createTasks',
      btnVariant: 'outlined',
    },
  ];

  return (
    <Paper
      elevation={0}
      component={Stack}
      justifyContent="center"
      sx={{
        px: { xs: 2.5, md: 4 },
        py: { xs: 2.5, md: 3 },
        border: (theme) => `1px solid ${theme.palette.hairline}`,
      }}
    >
      <Stack gap={2.5} divider={<Divider flexItem orientation="horizontal" />}>
        {taskArray.map((item, index) => (
          <ActionButton key={index} {...item} />
        ))}
      </Stack>
    </Paper>
  );
};
